import { lcResourcePreviewFragment } from 'humanity/components/lcResourcePreview';
import { learningCenterPageFragment } from 'humanity/pages/learningCenterPage/learningCenterPage.fragment';
import { fetchGraphQL, getPageSettingsBySlug } from 'lib/contentful';
import capitalize from 'utils/capitalize';
import { ITEMS_PER_PAGE, PARENT_SLUGS, ParentSlug } from 'utils/learningCenterConstants';
import { serverLogger } from 'utils/serverLogger';

export const learningCenterPageByParentSlug = (slug: ParentSlug, preview = false) => `
  query {
    pageContainerCollection(
      where: { slug: "${slug}" }
      limit: 1
      preview: ${preview}
    ) {
      items {
        sys {
          id
        }
        page {
          __typename
          ${learningCenterPageFragment}
        }
      }
    }
  }
`;

const createFilterString = (existingFilterValues, query, whereArguments) => {
  const filterStrings = existingFilterValues
    .split(',')
    .map((item) => `"${item}"`)
    .join(',');
  const newQuery = whereArguments
    ? `${query ? `${query}, ` : ''} ${whereArguments}: [${filterStrings}]`
    : query;

  return newQuery;
};

const filterNameGraphQlArguments = {
  categoryType: 'categories_contains_some',
  contentType: 'resourceType_in',
  topic: 'subcategories_contains_some',
};

export const lcResourcePreviewQuery = (
  parentSlug: ParentSlug,
  filterTypes,
  preview = false
) => {
  const page = filterTypes.page || 1;
  const skip = (page - 1) * ITEMS_PER_PAGE;

  let queryFilters;
  if (filterTypes.slug !== 'all') {
    if (parentSlug === PARENT_SLUGS.CATEGORIES) {
      queryFilters = `categories_contains_all: "${filterTypes.slug}"`;
    }
    if (parentSlug === PARENT_SLUGS.RESOURCES) {
      queryFilters = `resourceType_in: "${capitalize(filterTypes.slug)}"`;
    }
  }

  if (Object.keys(filterTypes).length) {
    queryFilters = Object.entries(filterTypes).reduce((query, filter) => {
      if (filter[0] === 'page') {
        return query;
      }
      return createFilterString(filter[1], query, filterNameGraphQlArguments[filter[0]]);
    }, queryFilters);
  }

  return `query {
  learningCenterResourceCollection(limit: ${ITEMS_PER_PAGE}, skip: ${skip}, order: lastModified_DESC, preview: ${preview}${
    queryFilters ? `, where: {${queryFilters}}` : ''
  } ) {
    total
    items {
      ${lcResourcePreviewFragment}
    }
  }
}
`;
};

export const getLearningCenterPageBySlug = async (
  path,
  parentSlug,
  routerQuery,
  preview = false
) => {
  const query = learningCenterPageByParentSlug(path, preview);

  const entry = await fetchGraphQL(query, preview);
  const cards = await fetchGraphQL(
    lcResourcePreviewQuery(parentSlug, routerQuery, preview),
    preview
  );
  return { ...entry?.data?.pageContainerCollection?.items?.[0]?.page, ...cards?.data };
};

export const getLearningCenterPageSSR = (basePath, parentSlug) => async (ctx) => {
  const preview = ctx.preview || false;
  const { slug } = ctx.params;
  const path = `${basePath}/${parentSlug}/${slug}`;
  try {
    const [pageSettings, pageContent] = await Promise.all([
      getPageSettingsBySlug(path, preview),
      getLearningCenterPageBySlug(path, parentSlug, ctx.query, preview),
    ]);
    const page = typeof ctx.query?.page !== 'undefined' ? ctx.query?.page : null;
    return {
      props: { pageSettings, pageContent, parentSlug, preview, page },
    };
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(`Error fetching ${path} page`, err, err.response?.data);
    serverLogger.error(`Error fetching ${path} page`, err, err.response?.data);
    throw new Error(`Error fetching ${path} page`, { cause: err });
  }
};
